import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    TextField,
    Button,
    Alert,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Divider,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    FormControlLabel,
    Switch
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ImageIcon from '@mui/icons-material/Image';
import { adminInstance } from '../../../utils/axios';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

const EditExam = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [exam, setExam] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
    const [openAIDialog, setOpenAIDialog] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [aiPrompt, setAiPrompt] = useState('');
    const [numQuestions, setNumQuestions] = useState(5);
    const [generatingQuestions, setGeneratingQuestions] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);
    const [isEditingExam, setIsEditingExam] = useState(false);

    const [questionForm, setQuestionForm] = useState({
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: '',
        imageUrl: ''
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [removeImage, setRemoveImage] = useState(false);

    const [examForm, setExamForm] = useState({
        title: '',
        description: '',
        numberOfQuestions: 0,
        price: 0,
        category: '',
        isActive: false,
        tags: []
    });

    const [newTag, setNewTag] = useState('');

    useEffect(() => {
        fetchExam();
    }, [id]);

    const fetchExam = async () => {
        try {
            const response = await adminInstance.get(`/api/admin/exams/${id}`);
            setExam(response.data);
            setExamForm({
                title: response.data.title,
                description: response.data.description,
                numberOfQuestions: response.data.numberOfQuestions,
                price: response.data.price,
                category: response.data.category,
                isActive: response.data.isActive,
                tags: response.data.tags || []
            });
            setQuestions(response.data.questions || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Error fetching exam');
        } finally {
            setLoading(false);
        }
    };

    const handleExamChange = (e) => {
        const { name, value } = e.target;
        setExamForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSaveExam = async () => {
        try {
            setSaving(true);
            await adminInstance.put(`/api/admin/exams/${id}`, examForm);
            await adminInstance.put(`/api/admin/exams/${id}`, examForm);
            setSuccess('Exam information updated successfully');
            fetchExam();
        } catch (err) {
            setError(err.response?.data?.message || 'Error updating exam information');
        } finally {
            setSaving(false);
        }
    };

    const handleQuestionChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('option')) {
            const index = parseInt(name.split('_')[1]);
            const newOptions = [...questionForm.options];
            newOptions[index] = value;
            setQuestionForm(prev => ({
                ...prev,
                options: newOptions
            }));
        } else {
            setQuestionForm(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleAddOption = () => {
        setQuestionForm(prev => ({
            ...prev,
            options: [...prev.options, '']
        }));
    };

    const handleRemoveOption = (index) => {
        setQuestionForm(prev => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== index)
        }));
    };

    const resetImageState = () => {
        setImageFile(null);
        setImagePreview('');
        setRemoveImage(false);
    };

    const handleOpenQuestionDialog = (question = null) => {
        if (question) {
            setEditingQuestion(question);
            setQuestionForm({
                question: question.question,
                options: question.options,
                correctAnswer: question.correctAnswer,
                explanation: question.explanation,
                imageUrl: question.imageUrl || ''
            });
            setImagePreview(question.imageUrl || '');
            setImageFile(null);
            setRemoveImage(false);
        } else {
            setEditingQuestion(null);
            setQuestionForm({
                question: '',
                options: ['', '', '', ''],
                correctAnswer: 0,
                explanation: '',
                imageUrl: ''
            });
            resetImageState();
        }
        setOpenQuestionDialog(true);
    };

    const handleCloseQuestionDialog = () => {
        setOpenQuestionDialog(false);
        setEditingQuestion(null);
        resetImageState();
    };

    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            setError('Please select an image file (JPEG, PNG, WebP, or GIF)');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be 5 MB or smaller');
            return;
        }

        setImageFile(file);
        setRemoveImage(false);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview('');
        setRemoveImage(true);
        setQuestionForm((prev) => ({ ...prev, imageUrl: '' }));
    };

    const handleSubmitQuestion = async () => {
        try {
            const useMultipart = Boolean(imageFile) || removeImage;
            let response;

            if (useMultipart) {
                const formData = new FormData();
                formData.append('question', questionForm.question);
                formData.append('options', JSON.stringify(questionForm.options));
                formData.append('correctAnswer', String(questionForm.correctAnswer));
                formData.append('explanation', questionForm.explanation || '');
                if (removeImage && !imageFile) {
                    formData.append('removeImage', 'true');
                }
                if (imageFile) {
                    formData.append('image', imageFile);
                }

                if (editingQuestion) {
                    response = await adminInstance.put(
                        `/api/admin/exams/${id}/questions/${editingQuestion._id}`,
                        formData
                    );
                    setQuestions(questions.map((q) =>
                        q._id === editingQuestion._id ? { ...q, ...response.data } : q
                    ));
                } else {
                    response = await adminInstance.post(`/api/admin/exams/${id}/questions`, formData);
                    setQuestions([...questions, response.data]);
                }
            } else if (editingQuestion) {
                await adminInstance.put(`/api/admin/exams/${id}/questions/${editingQuestion._id}`, {
                    question: questionForm.question,
                    options: questionForm.options,
                    correctAnswer: questionForm.correctAnswer,
                    explanation: questionForm.explanation
                });
                setQuestions(questions.map((q) =>
                    q._id === editingQuestion._id
                        ? {
                            ...q,
                            question: questionForm.question,
                            options: questionForm.options,
                            correctAnswer: questionForm.correctAnswer,
                            explanation: questionForm.explanation
                        }
                        : q
                ));
            } else {
                response = await adminInstance.post(`/api/admin/exams/${id}/questions`, {
                    question: questionForm.question,
                    options: questionForm.options,
                    correctAnswer: questionForm.correctAnswer,
                    explanation: questionForm.explanation
                });
                setQuestions([...questions, response.data]);
            }

            handleCloseQuestionDialog();
            setSuccess('Question saved successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Error saving question');
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        try {
            await adminInstance.delete(`/api/admin/exams/${id}/questions/${questionId}`);
            setQuestions(questions.filter(q => q._id !== questionId));
            setSuccess('Question deleted successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Error deleting question');
        }
    };

    const handleGenerateQuestions = async () => {
        setGeneratingQuestions(true);
        try {
            const response = await adminInstance.post(`/api/admin/exams/${id}/generate-questions`, {
                prompt: aiPrompt,
                numQuestions
            });
            setQuestions([...questions, ...response.data]);
            setOpenAIDialog(false);
            setSuccess('Questions generated successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Error generating questions');
        } finally {
            setGeneratingQuestions(false);
        }
    };

    const handlePublishExam = async () => {
        console.log("Publish button clicked");
        console.log("Current exam state:", { 
            examId: id, 
            questionsCount: questions.length, 
            requiredQuestions: exam.numberOfQuestions,
            isActive: examForm.isActive 
        });
        
        if (questions.length < exam.numberOfQuestions) {
            setError(`Cannot publish exam. Need ${exam.numberOfQuestions} questions, but only have ${questions.length}`);
            return;
        }

        try {
            console.log("Making API call to publish exam...");
            const response = await adminInstance.put(`/api/admin/exams/${id}/publish`);
            console.log("Publish API response:", response.data);
            
            setSuccess('Exam published successfully');
            
            // Update the local state to reflect the exam is now active/published
            setExamForm(prev => ({
                ...prev,
                isActive: true
            }));
            
            fetchExam();
        } catch (err) {
            console.error("Error publishing exam:", err.response || err);
            setError(err.response?.data?.message || 'Error publishing exam');
        }
    };

    const handleApproveQuestion = async (questionId) => {
        try {
            const response = await adminInstance.put(
                `/api/admin/exams/${id}/questions/${questionId}/approve`,
                { action: 'approve' }
            );

            setQuestions(questions.map(q => 
                q._id === questionId ? response.data : q
            ));
        } catch (error) {
            console.error('Error approving question:', error);
            setError('Failed to approve question');
        }
    };

    const handleRejectQuestion = async (questionId) => {
        if (!rejectionReason.trim()) {
            setError('Please provide a reason for rejection');
            return;
        }

        try {
            const response = await adminInstance.put(
                `/api/admin/exams/${id}/questions/${questionId}/approve`,
                { 
                    action: 'reject',
                    rejectionReason: rejectionReason.trim()
                }
            );

            setQuestions(questions.filter(q => q._id !== questionId));
            setRejectionReason('');
        } catch (error) {
            console.error('Error rejecting question:', error);
            setError('Failed to reject question');
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !examForm.tags.includes(newTag.trim())) {
            setExamForm(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setExamForm(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container>
            <Box display="flex" alignItems="center" mb={3}>
                <IconButton onClick={() => navigate('/admin/exams')} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1">
                    Edit Exam
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            {/* Exam Information Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6">
                        Exam Information
                    </Typography>
                    <Button
                        startIcon={<EditIcon />}
                        onClick={() => setIsEditingExam(!isEditingExam)}
                        variant={isEditingExam ? "outlined" : "contained"}
                    >
                        {isEditingExam ? 'Cancel Editing' : 'Edit Exam Information'}
                    </Button>
                </Box>

                {isEditingExam ? (
                    <Box sx={{ display: 'block' }}>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                label="Exam Title"
                                name="title"
                                value={examForm.title}
                                onChange={handleExamChange}
                                required
                            />
                        </Box>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={examForm.description}
                                onChange={handleExamChange}
                                multiline
                                rows={3}
                                required
                            />
                        </Box>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                label="Category"
                                name="category"
                                value={examForm.category}
                                onChange={handleExamChange}
                                required
                            />
                        </Box>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                label="Number of Questions"
                                name="numberOfQuestions"
                                type="number"
                                value={examForm.numberOfQuestions}
                                onChange={handleExamChange}
                                required
                                inputProps={{ min: 1 }}
                            />
                        </Box>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                label="Price"
                                name="price"
                                type="number"
                                value={examForm.price}
                                onChange={handleExamChange}
                                required
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Box>
                        <Box mb={2}>
                            <TextField
                                fullWidth
                                label="Add Tag"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddTag();
                                    }
                                }}
                            />
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {examForm.tags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        label={tag}
                                        onDelete={() => handleRemoveTag(tag)}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Box mb={2}>
                            <ToggleButtonGroup
                                value={examForm.isActive}
                                exclusive
                                onChange={(e, newValue) => {
                                    if (newValue !== null) {
                                        setExamForm(prev => ({
                                            ...prev,
                                            isActive: newValue
                                        }));
                                    }
                                }}
                                fullWidth
                            >
                                <ToggleButton value={false} fullWidth>
                                    <LockOpenIcon sx={{ mr: 1 }} />
                                    Draft
                                </ToggleButton>
                                <ToggleButton value={true} fullWidth>
                                    <LockIcon sx={{ mr: 1 }} />
                                    Published
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={() => {
                                    handleSaveExam();
                                    setIsEditingExam(false);
                                }}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'block' }}>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                            <Typography variant="body1">{examForm.title}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                            <Typography variant="body1">{examForm.description}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                            <Typography variant="body1">{examForm.category}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="text.secondary">Number of Questions</Typography>
                            <Typography variant="body1">{examForm.numberOfQuestions}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="text.secondary">Price</Typography>
                            <Typography variant="body1">£{Number(examForm.price).toFixed(2)}</Typography>
                        </Box>
                        <Box mb={2}>
                            <Typography variant="subtitle2" color="text.secondary">Tags</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {examForm.tags.map((tag, index) => (
                                    <Chip key={index} label={tag} />
                                ))}
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                            <Typography 
                                variant="body1" 
                                color={examForm.isActive ? 'success.main' : 'text.primary'}
                            >
                                {examForm.isActive ? 'Published' : 'Draft'}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Paper>

            {/* Questions Section */}
            <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6">
                        Questions ({questions.length}/{examForm.numberOfQuestions})
                    </Typography>
                    <Box>
                        <Button
                            variant="outlined"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => setOpenAIDialog(true)}
                            sx={{ mr: 2 }}
                        >
                            Generate Questions
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenQuestionDialog()}
                        >
                            Add Question
                        </Button>
                    </Box>
                </Box>

                <List>
                    {questions.map((question, index) => (
                        <React.Fragment key={question._id}>
                            <ListItem>
                                <Box sx={{ width: '100%', pr: 10 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        Question {index + 1}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body1" component="div">
                                            {question.question}
                                        </Typography>
                                        {question.imageUrl && (
                                            <Box
                                                component="img"
                                                src={question.imageUrl}
                                                alt={`Question ${index + 1}`}
                                                sx={{
                                                    mt: 1.5,
                                                    maxWidth: 240,
                                                    maxHeight: 160,
                                                    objectFit: 'contain',
                                                    borderRadius: 1,
                                                    display: 'block'
                                                }}
                                            />
                                        )}
                                        <Box mt={1}>
                                            {question.options.map((option, optIndex) => (
                                                <Typography
                                                    key={optIndex}
                                                    variant="body2"
                                                    component="div"
                                                    color={optIndex === question.correctAnswer ? 'primary' : 'text.secondary'}
                                                >
                                                    {optIndex === question.correctAnswer ? '✓ ' : '  '}
                                                    {option}
                                                </Typography>
                                            ))}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                            Explanation: {question.explanation}
                                        </Typography>
                                        {question.isAIGenerated && (
                                            <Box mt={1}>
                                                {question.approvalStatus === 'pending' && (
                                                    <Box display="flex" gap={1}>
                                                        <Button
                                                            size="small"
                                                            startIcon={<CheckCircleIcon />}
                                                            color="success"
                                                            onClick={() => handleApproveQuestion(question._id)}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            startIcon={<CancelIcon />}
                                                            color="error"
                                                            onClick={() => {
                                                                setSelectedQuestionId(question._id);
                                                                setShowRejectionModal(true);
                                                            }}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Box>
                                                )}
                                                {question.approvalStatus === 'approved' && (
                                                    <Typography variant="body2" color="success.main" component="div">
                                                        ✓ Approved
                                                    </Typography>
                                                )}
                                                {question.approvalStatus === 'rejected' && (
                                                    <Typography variant="body2" color="error.main" component="div">
                                                        ✗ Rejected: {question.rejectionReason}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleOpenQuestionDialog(question)}
                                        sx={{ mr: 1 }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleDeleteQuestion(question._id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                            {index < questions.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>

                {questions.length >= examForm.numberOfQuestions && !examForm.isActive && (
                    <Box display="flex" justifyContent="flex-end" mt={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePublishExam}
                        >
                            Publish Exam
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Question Dialog */}
            <Dialog open={openQuestionDialog} onClose={handleCloseQuestionDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingQuestion ? 'Edit Question' : 'Add New Question'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Question"
                            name="question"
                            value={questionForm.question}
                            onChange={handleQuestionChange}
                            multiline
                            rows={3}
                            required
                        />

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Question Image (optional)
                            </Typography>
                            {(imagePreview || questionForm.imageUrl) && !removeImage && (
                                <Box
                                    component="img"
                                    src={imagePreview || questionForm.imageUrl}
                                    alt="Question preview"
                                    sx={{
                                        mb: 1.5,
                                        maxWidth: '100%',
                                        maxHeight: 240,
                                        objectFit: 'contain',
                                        borderRadius: 1,
                                        display: 'block'
                                    }}
                                />
                            )}
                            <Box display="flex" gap={1} flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<ImageIcon />}
                                >
                                    {imagePreview || questionForm.imageUrl ? 'Replace Image' : 'Upload Image'}
                                    <input
                                        type="file"
                                        hidden
                                        accept={ACCEPTED_IMAGE_TYPES}
                                        onChange={handleImageSelect}
                                    />
                                </Button>
                                {(imagePreview || questionForm.imageUrl || imageFile) && (
                                    <Button color="error" onClick={handleRemoveImage}>
                                        Remove Image
                                    </Button>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                JPEG, PNG, WebP, or GIF — max 5 MB
                            </Typography>
                        </Box>

                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            Options
                        </Typography>
                        {questionForm.options.map((option, index) => (
                            <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                                <TextField
                                    fullWidth
                                    label={`Option ${index + 1}`}
                                    name={`option_${index}`}
                                    value={option}
                                    onChange={handleQuestionChange}
                                    required
                                />
                                <IconButton
                                    onClick={() => handleRemoveOption(index)}
                                    disabled={questionForm.options.length <= 4}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddOption}
                            disabled={questionForm.options.length >= 6}
                            sx={{ mt: 1 }}
                        >
                            Add Option
                        </Button>

                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Correct Answer</InputLabel>
                            <Select
                                name="correctAnswer"
                                value={questionForm.correctAnswer}
                                onChange={handleQuestionChange}
                                label="Correct Answer"
                                required
                            >
                                {questionForm.options.map((_, index) => (
                                    <MenuItem key={index} value={index}>
                                        Option {index + 1}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Explanation"
                            name="explanation"
                            value={questionForm.explanation}
                            onChange={handleQuestionChange}
                            multiline
                            rows={3}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseQuestionDialog}>Cancel</Button>
                    <Button onClick={handleSubmitQuestion} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* AI Generation Dialog */}
            <Dialog open={openAIDialog} onClose={() => setOpenAIDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Questions with AI</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Prompt"
                            multiline
                            rows={4}
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            placeholder="Describe the type of questions you want to generate..."
                            required
                        />
                        <TextField
                            fullWidth
                            label="Number of Questions"
                            type="number"
                            value={numQuestions}
                            onChange={(e) => setNumQuestions(parseInt(e.target.value))}
                            inputProps={{ min: 1, max: 10 }}
                            sx={{ mt: 2 }}
                            required
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAIDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleGenerateQuestions}
                        variant="contained"
                        disabled={generatingQuestions}
                    >
                        {generatingQuestions ? 'Generating...' : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rejection Reason Modal */}
            <Dialog open={showRejectionModal} onClose={() => setShowRejectionModal(false)}>
                <DialogTitle>Reject Question</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for Rejection"
                        fullWidth
                        multiline
                        rows={4}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRejectionModal(false)}>Cancel</Button>
                    <Button 
                        onClick={() => {
                            handleRejectQuestion(selectedQuestionId);
                            setShowRejectionModal(false);
                        }}
                        color="error"
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default EditExam; 